<template>
  <a-drawer
    :visible="visible"
    title="New Disbursement Request"
    placement="right"
    width="1000"
    @close="handleClose"
  >
    <div style="padding: 0 16px">
      <!-- Step Indicator -->
      <div style="margin-bottom: 24px">
        <a-steps :current="currentStep" size="small">
          <a-step title="Select Factoring Request" />
          <a-step title="Enter Disbursement Details" />
          <a-step title="Review & Submit" />
        </a-steps>
      </div>

      <!-- Single Scrollable Content Container -->
      <div
        style="height: calc(100vh - 250px); overflow: auto; padding-right: 8px"
      >
        <!-- Step 0: Select Invoice -->
        <div v-if="currentStep === 0">
          <div
            style="
              background-color: #f6ffed;
              border: 1px solid #b7eb8f;
              border-radius: 6px;
              padding: 12px 16px;
              margin-bottom: 24px;
              color: #52c41a;
              font-size: 14px;
            "
          >
            Single select on factoring request.
          </div>

          <!-- Loading State -->
          <div v-if="loading" style="text-align: center; padding: 40px">
            <a-spin size="large" />
            <div style="margin-top: 16px; color: #6b7280">
              Loading eligible invoices...
            </div>
          </div>

          <!-- Error State -->
          <div
            v-else-if="error"
            style="
              background-color: #fef2f2;
              border: 1px solid #fecaca;
              border-radius: 6px;
              padding: 16px;
              margin-bottom: 24px;
              color: #dc2626;
              text-align: center;
            "
          >
            <a-icon type="exclamation-circle" style="margin-right: 8px" />
            {{ error }}
            <a-button
              type="link"
              @click="fetchEligibleInvoices"
              style="margin-left: 8px; color: #dc2626"
            >
              Retry
            </a-button>
          </div>

          <!-- Data Table -->
          <div v-else>
            <!-- No data message -->
            <div
              v-if="availableInvoices.length === 0"
              style="
                background-color: #f0f9ff;
                border: 1px solid #0ea5e9;
                border-radius: 6px;
                padding: 16px;
                margin-bottom: 16px;
                color: #0369a1;
                text-align: center;
              "
            >
              No eligible invoices found.
            </div>

            <a-table
              :columns="invoiceSelectionColumns"
              :data-source="safeTableData"
              :pagination="tablePagination"
              :row-selection="{
                type: 'radio',
                selectedRowKeys: selectedInvoiceKeys,
                onChange: onInvoiceSelectionChange,
              }"
              row-key="invoiceId"
              size="middle"
              :loading="loading"
              @change="handleTableChange"
            >
              <template slot="customerBusinessName" slot-scope="text, record">
                {{ record.customerBusinessName }}
              </template>
              <template slot="dateApplied" slot-scope="text, record">
                {{
                  record.dateApplied
                    ? new Date(record.dateApplied).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "N/A"
                }}
              </template>
              <template slot="totalOrderValue" slot-scope="text, record">
                {{
                  record.totalOrderValue
                    ? formatAmount(record.totalOrderValue, "KES")
                    : "N/A"
                }}
              </template>
              <template slot="advanceAmount" slot-scope="text, record">
                {{
                  record.advanceAmount
                    ? formatAmount(record.advanceAmount, "KES")
                    : "N/A"
                }}
              </template>
              <template slot="remainingAmount" slot-scope="text, record">
                {{
                  record.remainingAmount
                    ? formatAmount(record.remainingAmount, "KES")
                    : "N/A"
                }}
              </template>

              <template slot="disbursementStatus" slot-scope="text, record">
                <a-tag
                  :color="
                    record.disbursementStatus === 'Pending Payable'
                      ? 'orange'
                      : 'blue'
                  "
                >
                  {{ record.disbursementStatus }}
                </a-tag>
              </template>
            </a-table>
          </div>
        </div>

        <!-- Step 1: Enter Details -->
        <div v-if="currentStep === 1">
          <!-- Invoice Summary Card -->
          <div
            style="
              background-color: #f8f9fa;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 24px;
              border: 1px solid #e9ecef;
            "
          >
            <h4
              style="
                margin: 0 0 16px 0;
                font-weight: 600;
                color: #000;
                font-size: 16px;
              "
            >
              Invoice Summary
            </h4>

            <a-row :gutter="24">
              <a-col :span="12">
                <div style="margin-bottom: 12px">
                  <span style="color: #6b7280; font-size: 14px">Invoice #</span>
                  <div
                    style="color: #26a246; font-weight: 600; font-size: 14px"
                  >
                    {{ selectedInvoice ? selectedInvoice.invoiceId : "" }}
                  </div>
                </div>
                <div>
                  <span style="color: #6b7280; font-size: 14px"
                    >Invoice Date</span
                  >
                  <div style="color: #000; font-weight: 600; font-size: 14px">
                    {{
                      selectedInvoice
                        ? formatInvoiceDate(selectedInvoice.dateApplied)
                        : ""
                    }}
                  </div>
                </div>
              </a-col>
              <a-col :span="12">
                <div style="margin-bottom: 12px">
                  <span style="color: #6b7280; font-size: 14px"
                    >Buyer Name</span
                  >
                  <div style="color: #000; font-weight: 600; font-size: 14px">
                    {{
                      selectedInvoice
                        ? selectedInvoice.customerBusinessName
                        : ""
                    }}
                  </div>
                </div>
                <div>
                  <span style="color: #6b7280; font-size: 14px"
                    >Outstanding Balance</span
                  >
                  <div
                    style="color: #26a246; font-weight: 600; font-size: 14px"
                  >
                    {{ formatAmount(selectedInvoice.remainingAmount, "KES") }}
                  </div>
                </div>
              </a-col>
            </a-row>
          </div>

          <a-form
            layout="vertical"
            :model="disbursementForm"
            ref="disbursementForm"
          >
            <a-form-item label="Amount to Disburse*" required>
              <a-input-number
                v-model="disbursementForm.amount"
                placeholder="0.00"
                style="width: 100%"
                :min="1"
                :max="selectedInvoice ? selectedInvoice.remainingAmount : 0"
                :precision="2"
                :step="1"
              />
              <div style="color: #6b7280; font-size: 12px; margin-top: 4px">
                Enter a value between {{ formatAmount(1) }} and
                {{
                  formatAmount(
                    selectedInvoice ? selectedInvoice.remainingAmount : 0
                  )
                }}
              </div>
            </a-form-item>

            <a-form-item label="Notes (Optional)">
              <a-textarea
                v-model="disbursementForm.notes"
                placeholder="Add comments..."
                :rows="3"
                allow-clear
              />
            </a-form-item>
          </a-form>
        </div>

        <!-- Step 2: Review & Submit -->
        <div v-if="currentStep === 2">
          <h3 style="margin-bottom: 24px; color: #05004e">
            Review Disbursement Request
          </h3>

          <!-- Invoice Summary Section -->
          <div
            style="
              background-color: #ffffff;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 16px;
              border: 1px solid #e5e7eb;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            "
          >
            <div
              style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;
              "
            >
              <h4
                style="
                  margin: 0;
                  font-weight: 600;
                  color: #374151;
                  font-size: 16px;
                "
              >
                Invoice Summary
              </h4>
              <a-icon
                :type="collapsedSections.invoiceSummary ? 'down' : 'up'"
                style="color: #6b7280; cursor: pointer"
                @click="toggleSection('invoiceSummary')"
              />
            </div>

            <div
              v-show="!collapsedSections.invoiceSummary"
              style="margin-bottom: 16px"
            >
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 12px;
                  padding: 8px 0;
                "
              >
                <span style="color: #6b7280; font-size: 14px"
                  >Invoice Number</span
                >
                <span style="color: #26a246; font-weight: 600; font-size: 14px">
                  {{ selectedInvoice ? selectedInvoice.invoiceId : "" }}
                </span>
              </div>
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 12px;
                  padding: 8px 0;
                "
              >
                <span style="color: #6b7280; font-size: 14px"
                  >Invoice Date</span
                >
                <span style="color: #000; font-weight: 600; font-size: 14px">
                  {{
                    selectedInvoice
                      ? formatInvoiceDate(selectedInvoice.dateApplied)
                      : ""
                  }}
                </span>
              </div>
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 12px;
                  padding: 8px 0;
                "
              >
                <span style="color: #6b7280; font-size: 14px">Buyer Name</span>
                <span style="color: #000; font-weight: 600; font-size: 14px">
                  {{
                    selectedInvoice ? selectedInvoice.customerBusinessName : ""
                  }}
                </span>
              </div>
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  padding: 8px 0;
                "
              >
                <span style="color: #6b7280; font-size: 14px"
                  >Total Outstanding Balance</span
                >
                <span style="color: #26a246; font-weight: 600; font-size: 14px">
                  {{
                    formatAmount(
                      selectedInvoice ? selectedInvoice.remainingAmount : 0,
                      "KES"
                    )
                  }}
                </span>
              </div>
            </div>
          </div>

          <!-- Disbursement Details Section -->
          <div
            style="
              background-color: #ffffff;
              border-radius: 8px;
              padding: 20px;
              margin-bottom: 16px;
              border: 1px solid #e5e7eb;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            "
          >
            <div
              style="
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 16px;
              "
            >
              <h4
                style="
                  margin: 0;
                  font-weight: 600;
                  color: #374151;
                  font-size: 16px;
                "
              >
                Disbursement Details
              </h4>
              <a-icon
                :type="collapsedSections.disbursementDetails ? 'down' : 'up'"
                style="color: #6b7280; cursor: pointer"
                @click="toggleSection('disbursementDetails')"
              />
            </div>

            <div
              v-show="!collapsedSections.disbursementDetails"
              style="margin-bottom: 16px"
            >
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 12px;
                  padding: 8px 0;
                "
              >
                <span style="color: #6b7280; font-size: 14px"
                  >Amount to Disburse</span
                >
                <span style="color: #000; font-weight: 600; font-size: 14px">
                  {{ formatAmount(disbursementForm.amount || 0, "KES") }}
                </span>
              </div>
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 12px;
                  padding: 8px 0;
                "
              >
                <span style="color: #6b7280; font-size: 14px"
                  >Scheduled Send Date</span
                >
                <span style="color: #000; font-weight: 600; font-size: 14px">
                  {{ formatDate(disbursementForm.expectedDate) }}
                </span>
              </div>
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 12px;
                  padding: 8px 0;
                "
              >
                <span style="color: #6b7280; font-size: 14px">Notes</span>
                <span style="color: #000; font-size: 14px">
                  {{ disbursementForm.notes || "None" }}
                </span>
              </div>
              <div
                style="
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  padding: 8px 0;
                "
              >
                <span style="color: #6b7280; font-size: 14px"
                  >Attached Files</span
                >
                <span style="color: #000; font-size: 14px">
                  <a-icon
                    type="file-pdf"
                    style="color: #ef4444; margin-right: 8px"
                  />
                  <a-icon type="file-excel" style="color: #10b981" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div
        style="
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          border-top: 1px solid #e8e8e8;
          padding: 16px;
          background: #fff;
          display: flex;
          justify-content: space-between;
          align-items: center;
        "
      >
        <a-button @click="handleClose">
          <a-icon type="arrow-left" /> Back to Dashboard
        </a-button>

        <div>
          <a-button
            v-if="currentStep > 0"
            style="margin-right: 8px"
            @click="previousStep"
          >
            Previous
          </a-button>

          <a-button
            v-if="currentStep < 2"
            type="primary"
            @click="nextStep"
            :disabled="!canProceedToNextStep"
            style="background-color: #26a246; border-color: #26a246"
          >
            Next <a-icon type="arrow-right" />
          </a-button>

          <a-button
            v-if="currentStep === 2"
            type="primary"
            @click="submitDisbursement"
            :loading="submitting"
            style="background-color: #26a246; border-color: #26a246"
          >
            Submit Disbursement
          </a-button>
        </div>
      </div>
    </div>
  </a-drawer>
</template>

<script>
import FinancingAndPaymentsService from "@/api/financing-and-payments.service.js";
import { formatAmount } from "@/utils";

export default {
  name: "NewDisbursementRequest",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    sellerId: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      currentStep: 0,
      selectedInvoiceKeys: [],
      disbursementForm: {
        amount: null,
        notes: "",
      },
      submitting: false,
      // Collapsible states for step 3
      collapsedSections: {
        invoiceSummary: false,
        disbursementDetails: false,
        disbursementTarget: false,
      },
      // API data
      availableInvoices: [],
      invoiceSelectionColumns: [
        {
          title: "Invoice #",
          dataIndex: "invoiceId",
          key: "invoiceId",
          width: 120,
        },
        {
          title: "Seller Name",
          dataIndex: "sellerName",
          key: "sellerName",
          width: 200,
          scopedSlots: { customRender: "sellerName" },
        },
        {
          title: "Buyer Name",
          dataIndex: "customerBusinessName",
          key: "customerBusinessName",
          width: 200,
          scopedSlots: { customRender: "customerBusinessName" },
        },
        {
          title: "Invoice Date",
          dataIndex: "dateApplied",
          key: "dateApplied",
          width: 120,
          scopedSlots: { customRender: "dateApplied" },
        },
        {
          title: "Total Amount",
          dataIndex: "totalOrderValue",
          key: "totalOrderValue",
          width: 120,
          scopedSlots: { customRender: "totalOrderValue" },
        },
        {
          title: "Advance Amount",
          dataIndex: "advanceAmount",
          key: "advanceAmount",
          width: 120,
          scopedSlots: { customRender: "advanceAmount" },
        },
        {
          title: "Outstanding",
          dataIndex: "remainingAmount",
          key: "remainingAmount",
          width: 120,
          scopedSlots: { customRender: "remainingAmount" },
        },

        {
          title: "Disbursement Status",
          dataIndex: "disbursementStatus",
          key: "disbursementStatus",
          width: 150,
          scopedSlots: { customRender: "disbursementStatus" },
        },
      ],
      // Loading and error states
      loading: false,
      error: null,
      // Pagination
      pagination: {
        pageIndex: 1,
        pageSize: 10,
        totalCount: 0,
      },
    };
  },
  computed: {
    selectedInvoice() {
      if (this.selectedInvoiceKeys.length === 0) return null;
      return this.availableInvoices.find(
        (invoice) => invoice.invoiceId === this.selectedInvoiceKeys[0]
      );
    },
    tablePagination() {
      return {
        current: this.pagination.pageIndex,
        pageSize: this.pagination.pageSize,
        total: this.pagination.totalCount,
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: false,
      };
    },
    canProceedToNextStep() {
      switch (this.currentStep) {
        case 0:
          return this.selectedInvoiceKeys.length > 0;
        case 1:
          return this.disbursementForm.amount;
        default:
          return true;
      }
    },
    // Ensure table data is always safe
    safeTableData() {
      return Array.isArray(this.availableInvoices)
        ? this.availableInvoices
        : [];
    },
  },
  methods: {
    formatAmount,
    handleClose() {
      this.$emit("close");
      this.resetForm();
    },

    onInvoiceSelectionChange(selectedRowKeys) {
      this.selectedInvoiceKeys = selectedRowKeys;
    },

    nextStep() {
      if (this.canProceedToNextStep) {
        this.currentStep++;
      }
    },

    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },

    getMethodLabel(method) {
      const methodMap = {
        bank_transfer: "Bank Transfer",
        mobile_money: "Mobile Money",
        check: "Check",
      };
      return methodMap[method] || method;
    },

    formatDate(date) {
      if (!date) return "Not set";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatInvoiceDate(date) {
      if (!date) return "Not set";
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    disabledDate(current) {
      // Disable past dates
      return current && current < new Date().setHours(0, 0, 0, 0);
    },

    async fetchEligibleInvoices() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          pageIndex: this.pagination.pageIndex,
          pageSize: this.pagination.pageSize,
        };

        // Add sellerId to params if provided
        console.log("sellerId", this.sellerId)
        if (this.sellerId) {
          params.sellerId = this.sellerId;
        }

        const response =
          await FinancingAndPaymentsService.getEligibleFactoredInvoicesForDisbursement(params);

        // Handle different response structures - following WalletTab pattern
        let invoices = [];

        if (response && response.data) {
          // Check for standard API response structure
          if (response.data.code === 200 || response.data.status === 200) {
            const data = response.data.data || response.data;
            invoices = data?.results;
            this.pagination.totalCount = data?.totalCount || 0;
            console.log(invoices)
          }

          // Flatten the nested data structure like WalletTab does
          const flattenedInvoices = [];
          (invoices || []).forEach((item) => {
            flattenedInvoices.push({
              invoiceId: item.invoiceId,
              customerBusinessName: item.invoice.customerBusinessName,
              sellerName: item.invoice.sellerName,
              dateApplied: item.dateApplied,
              totalOrderValue: item.invoice.totalOrderValue,
              advanceAmount: item.advanceAmount || 0,
              remainingAmount: item.remainingAmount || 0,
              disbursementStatus: item.disbursementStatus || "N/A",
              factoringId: item.factoring.id || "N/A",
              sellerId: item.factoring.usersetupid || "N/A",
              // Keep original item for reference
              originalData: item,
            });
          });

          // Ensure we always have an array and handle null/undefined
          this.availableInvoices = flattenedInvoices;
        } else {
          this.error = "No eligible invoices found.";
          this.availableInvoices = [];
          this.pagination.totalCount = 0;
        }
      } catch (error) {
        this.error = error.message || "Failed to fetch eligible invoices.";
        console.error("Error fetching eligible invoices:", error);
        this.availableInvoices = [];
        this.pagination.totalCount = 0;
      } finally {
        this.loading = false;
      }
    },

    async submitDisbursement() {
      this.submitting = true;
        const payload = {
          factoringId: String(this.selectedInvoice.factoringId),
          amount: this.disbursementForm.amount,
        };

        const response = await FinancingAndPaymentsService.createDisbursement(
          this.selectedInvoice.sellerId,
          payload
        );

        if (response.status === 200) {
          this.$message.success("Disbursement request created successfully!");
          this.handleClose();
          this.$emit("success");
        } else {
          this.$message.error(
            response.data?.message || "Failed to create disbursement"
          );
        }
        this.submitting = false;
    },

    resetForm() {
      this.currentStep = 0;
      this.selectedInvoiceKeys = [];
      this.disbursementForm = {
        amount: null,
        expectedDate: null,
        notes: "",
      };
      // Reset collapsible states
      this.collapsedSections = {
        invoiceSummary: false,
        disbursementDetails: false,
        disbursementTarget: false,
      };
    },

    toggleSection(sectionName) {
      this.collapsedSections[sectionName] =
        !this.collapsedSections[sectionName];
    },

    handleTableChange(pagination) {
      if (pagination.current !== this.pagination.pageIndex) {
        this.pagination.pageIndex = pagination.current;
      }
    },
  },
  mounted() {
    this.fetchEligibleInvoices();
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.fetchEligibleInvoices();
      }
    },
    "pagination.pageIndex"() {
      this.fetchEligibleInvoices();
    },
  },
};
</script>

<style scoped>
.ant-descriptions-item-label {
  font-weight: 600;
  color: #05004e;
}
</style>
